import { useEffect, useState } from 'react';
import { AppButton, AppSectionCard, AppSectionRow } from '@core/designsystem';
import { describe } from '@core/domain/result';
import { useData } from './data/repositories';
import { plural } from '@core/ui/RuDates';

/**
 * Проверка хранилища прямо в приложении.
 *
 * Нужна ровно затем, что база на iPhone ведёт себя иначе, чем в Node: OPFS есть только
 * с iOS 17, на iOS 16 данные уходят в IndexedDB, а увидеть, что именно случилось
 * у конкретного юриста, иначе нельзя. Экран временный — до шага 7, где появятся
 * настоящие экраны; тогда эти же цифры переедут в «Хранилище».
 */
export function StorageProbe() {
  const { db, files } = useData();
  const [cases, setCases] = useState<number | null>(null);
  const [bytes, setBytes] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Сколько файлов считает живыми уборка: расхождение с делом видно сразу. */
  const [live, setLive] = useState<number | null>(null);
  const [orphans, setOrphans] = useState<number | null>(null);
  const [documents, setDocuments] = useState<number | null>(null);
  const [journal, setJournal] = useState<string>('');

  useEffect(() => {
    void (async () => {
      setCases((await db.cases.list()).length);
      setBytes(await files.totalSizeBytes());

      const liveDocuments = await db.documents.countLive();
      setJournal(await db.journalMode());
      const liveFiles = await db.documents.liveFilesByCase();
      setLive([...liveFiles.values()].reduce((total, set) => total + set.size, 0));
      setDocuments(liveDocuments);
      setOrphans((await files.orphans(liveFiles)).length);
    })();
  }, [db, files]);

  const add = async () => {
    await db.cases.create({
      id: '',
      title: `Проверка ${new Date().toLocaleTimeString('ru')}`,
      clientId: null,
      type: 'CourtCase',
      status: 'Active',
      typeOther: null,
      description: null,
      notes: null,
      links: [],
      isPinned: false,
      sortOrder: 0,
    });
    setCases((await db.cases.list()).length);
  };

  /** Кладём файл ровно тем же путём, каким его положит сканер на шаге 11. */
  const addFile = async () => {
    const id = crypto.randomUUID();
    const result = await files.store('проверка', id, 'скан.pdf', new Blob([`страница ${id}`]));

    // Молча проглоченная ошибка записи файла — ровно то, чего в этом приложении быть
    // не должно: юрист обязан узнать, что документ не сохранился, в ту же секунду.
    if (!result.ok) {
      // На проверочном экране показываем и подробность: она нужна нам, а не юристу.
      setError(
        result.error.kind === 'Storage'
          ? `${describe(result.error)}: ${result.error.reason}`
          : describe(result.error),
      );
      return;
    }
    setBytes(await files.totalSizeBytes());
  };

  return (
    <AppSectionCard title="Хранилище">
      <AppSectionRow
        icon="folder"
        title={`Данные в ${db.storage}`}
        subtitle={
          cases === null ? undefined : `${plural(cases, 'дело', 'дела', 'дел')} в базе`
        }
      />
      <AppSectionRow
        icon="picture_as_pdf"
        title={`Файлы в ${files.kind}`}
        subtitle={bytes === null ? undefined : `${bytes} Б занято`}
      />
      <AppSectionRow
        icon="checklist"
        title={live === null ? 'Считаем…' : `Живых файлов: ${live} из ${documents ?? 0}`}
        subtitle={orphans === null ? undefined : `Осиротевших: ${orphans} · журнал ${journal}`}
      />
      {error ? <AppSectionRow icon="error" title={error} /> : null}
      <div className="catalogue__row">
        <AppButton onClick={add}>Записать дело</AppButton>
        <AppButton variant="text" onClick={addFile}>
          Записать файл
        </AppButton>
      </div>
    </AppSectionCard>
  );
}
